import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const MatterList = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMatters = async () => {
      if (!profile?.advisor_firm_id) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch ALL matters for the firm with client details
        const { data, error: fetchErr } = await supabase
          .from('matters')
          .select(`
            *,
            client:clients (
              first_name,
              last_name,
              email,
              phone,
              state_of_residence
            )
          `)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .order('created_at', { ascending: false });

        if (fetchErr) {
          console.error('MatterList fetch error:', fetchErr);
          throw fetchErr;
        }

        setMatters(data || []);
      } catch (err) {
        setError(err.message || "Failed to load plans list.");
      } finally {
        setLoading(false);
      }
    };

    fetchMatters();
  }, [profile]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'matter_created': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'completed': return 'bg-green-50 text-green-700 border-green-100';
      default: return 'bg-slate-50 text-slate-700 border-slate-100';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">All Plans</h1>
          <p className="text-sm text-slate-500">Complete history of estate planning workflows for your firm.</p>
        </div>
        <button 
          onClick={() => navigate('/matters/create')}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiPlus} className="-ml-1 mr-2 h-5 w-5" />
          New Plan
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2 shrink-0" />
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-20 text-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mx-auto h-10 w-10 text-slate-300 mb-4" />
            <p className="text-slate-500">Loading firm plans...</p>
          </div>
        ) : matters.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Plan Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Complexity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                  <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {matters.map((matter) => (
                  <tr 
                    key={matter.id} 
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/matters/${matter.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">
                        {matter.client?.first_name} {matter.client?.last_name}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-[200px]">{matter.client?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-700 font-medium">{matter.plan_type}</div>
                      <div className="text-xs text-slate-400 uppercase tracking-tighter">Estate Plan</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full border ${getStatusColor(matter.status)}`}>
                        {matter.status === 'matter_created' ? 'plan created' : matter.status?.replace('matter', 'plan').replaceAll('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-xs text-slate-600 bg-slate-100 py-0.5 px-2 rounded-md border border-slate-200 capitalize">
                        {matter.complexity_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {format(new Date(matter.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-slate-400 hover:text-slate-900 p-1">
                        <SafeIcon icon={FiIcons.FiChevronRight} className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-20 text-center text-slate-500 px-4">
            <SafeIcon icon={FiIcons.FiInbox} className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No plans found</h3>
            <p className="mt-1 text-slate-500">You haven't initiated any estate plans yet.</p>
            <button 
              onClick={() => navigate('/matters/create')}
              className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900"
            >
              Start first plan
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatterList;
